import React from 'react';
import { useTheme } from '../../providers/ThemeProvider';

interface ThemeToggleProps {
  className?: string;
  variant?: 'button' | 'switch' | 'icon';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = '', variant = 'icon' }) => {
  const { theme, toggleTheme, isDark } = useTheme();
  // Translation hook removed as it was not being used

  // Modern toggle switch variant
  if (variant === 'switch') {
    return (
      <div className={`flex items-center ${className}`}>
        <button
          onClick={toggleTheme}
          className="relative inline-flex items-center h-6 rounded-full w-11 focus:outline-none"
          aria-pressed={isDark}
          aria-label="Toggle theme"
        >
          <span className="sr-only">{isDark ? 'Switch to light mode' : 'Switch to dark mode'}</span>
          <span className={`${isDark ? 'bg-marune-vibrant-red' : 'bg-gray-300'} absolute h-6 w-11 mx-auto rounded-full transition-colors duration-300 ease-in-out`}></span>
          <span className={`${isDark ? 'translate-x-6 bg-white' : 'translate-x-1 bg-white'} absolute left-0 inline-block h-4 w-4 rounded-full transform transition-transform duration-300 ease-in-out mt-1 ml-1`}>
          </span>
        </button>
        <span className="ml-2 text-sm">
          {isDark ? 'Dark' : 'Light'}
        </span>
      </div>
    );
  }

  // Text button variant
  if (variant === 'button') {
    return (
      <button
        onClick={toggleTheme}
        className={`px-3 py-1 rounded-md transition-colors ${
          theme === 'dark'
            ? 'bg-marune-royal-blue text-white hover:bg-marune-blue-hover'
            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
        } ${className}`}
      >
        {theme === 'light' ? '🌙 Dark Mode' : '☀️ Light Mode'}
      </button>
    );
  }

  // Default icon variant
  return (
    <button 
      onClick={toggleTheme}
      className={`p-2 rounded-full transition-colors ${
        theme === 'dark' 
          ? 'text-marune-royal-blue hover:bg-gray-800' 
          : 'text-marune-royal-blue hover:bg-gray-100'
      } ${className}`}
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      {theme === 'light' ? (
        // Moon icon for dark mode
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
        </svg>
      ) : (
        // Sun icon for light mode
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="5"></circle>
          <line x1="12" y1="1" x2="12" y2="3"></line>
          <line x1="12" y1="21" x2="12" y2="23"></line>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
          <line x1="1" y1="12" x2="3" y2="12"></line>
          <line x1="21" y1="12" x2="23" y2="12"></line>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
        </svg>
      )}
    </button>
  );
};

export default ThemeToggle;